// @ts-ignore
import gsap, { ScrollTrigger, SplitText } from 'gsap/all'

gsap.registerPlugin(ScrollTrigger, SplitText)

function unwrapTag(element: HTMLElement, tagName: string): string[] {
	const trackedTexts: string[] = []
	const tags = Array.from(element.querySelectorAll(tagName))
	tags.forEach(tag => {
		const text = tag.textContent?.trim() || ''
		if (text) trackedTexts.push(text)
		const parent = tag.parentNode
		while (tag.firstChild) parent?.insertBefore(tag.firstChild, tag)
		parent?.removeChild(tag)
	})
	element.normalize()
	return trackedTexts
}

function restoreUnderlines(element: HTMLElement, underlinedWords: string[]): void {
	if (!underlinedWords.length) return
	element.querySelectorAll<HTMLElement>('.words').forEach(wordEl => {
		const text = wordEl.textContent?.trim().replace(/[.,;:!?]+$/, '') || ''
		if (underlinedWords.includes(text)) {
			wordEl.style.textDecoration = 'underline'
		}
	})
}

export function handleFillOnScroll(): void {
	const sections = document.querySelectorAll<HTMLElement>(
		'.text-fill-container'
	)

	sections?.forEach(section => {
		const textWrapper =
			section?.querySelector<HTMLElement>('.text-fill-content')
		const hiddenText = textWrapper?.querySelector<HTMLElement>(
			'.text-fill-item.fill-animation'
		)
		const mainText = textWrapper?.querySelector<HTMLElement>(
			'.text-fill-item.main'
		)

		if (!hiddenText || !mainText) return
		const isDesktop: boolean = window.innerWidth >= 1024

		const underlinedHidden = unwrapTag(hiddenText, 'u')
		new SplitText(hiddenText, {
			type: 'lines,words',
			linesClass: 'line',
			wordsClass: 'words',
		})
		restoreUnderlines(hiddenText, underlinedHidden)

		let underlinedMain: string[] = []
		if (window.innerWidth <= 1023) {
			underlinedMain = unwrapTag(mainText, 'u')
			new SplitText(mainText, {
				type: 'lines,words',
				linesClass: 'line',
				wordsClass: 'words',
			})
			restoreUnderlines(mainText, underlinedMain)
		}

		// Restructure anchor tags to be inside word elements, not wrapping them
		const allTextElements = [hiddenText, mainText]
		allTextElements.forEach(element => {
			if (!element) return
			// Find all anchors that contain word elements
			const anchorTags = Array.from(element.querySelectorAll<HTMLElement>('a'))
			anchorTags.forEach(anchor => {
				const wordInsideAnchor = anchor.querySelector<HTMLElement>('.words')
				if (wordInsideAnchor) {
					// Get anchor's parent and position
					const anchorParent = anchor.parentNode
					if (!anchorParent) return

					// Save word element's content
					const wordContent = Array.from(wordInsideAnchor.childNodes)
					
					// Create new anchor with same attributes
					const newAnchor = document.createElement('a')
					Array.from(anchor.attributes).forEach(attr => {
						newAnchor.setAttribute(attr.name, attr.value)
					})
					
					// Move word content into new anchor
					wordContent.forEach(node => {
						newAnchor.appendChild(node)
					})
					
					// Clear word element and put anchor inside it
					wordInsideAnchor.innerHTML = ''
					wordInsideAnchor.appendChild(newAnchor)
					
					// Replace original anchor with word element
					anchorParent.replaceChild(wordInsideAnchor, anchor)
				}
			})
		})

		const lines = Array.from(
			hiddenText.querySelectorAll<HTMLElement>('.line')
		)
		const scrollDistance = isDesktop
			? `+=${lines.length * 120}`
			: 'bottom 50%'

		const tl = gsap.timeline({
			scrollTrigger: {
				trigger: section,
				start: 'top 95%',
				end: scrollDistance,
				scrub: true,
			},
		})

		lines.forEach((line, index) => {
			if (index === 0) {
				gsap.set(line, {
					clipPath: 'inset(0 0% 0 0)',
				})
				return
			}

			const position = index === 0 ? undefined : '-=2'
			tl.to(
				line,
				{
					clipPath: 'inset(0 0% 0 0)',
					duration: 13,
					ease: 'power2.ease',
				},
				position
			)

			// Get all words in this line (starting from 2nd line)
			const words = Array.from(
				line.querySelectorAll<HTMLElement>('.words')
			)

			// Set initial state for words (fade out)
			gsap.set(words, {
				opacity: .5,
			})

			// Animate words fade in on scroll, starting when line animation begins
			words.forEach((word, wordIndex) => {
				const wordPosition = wordIndex === 0 ? position : '+=0.1'
				tl.to(
					word,
					{
						opacity: 1,
						duration: 1,
						ease: 'power2.ease',
					},
					wordPosition
				)
			})
		})
	})
}

export function mouseOverImages():void {
	const sections = document.querySelectorAll<HTMLElement>('.text-fill-container');

	sections.forEach(section => {
		const titles = section.querySelectorAll<HTMLElement>(
			'.text-fill-item.main .text-hover-image'
		)
		const images = section.querySelectorAll<HTMLElement>('.text-image-item')
		const mainImageContainer =
			section.querySelector<HTMLElement>('.text-fill-images')

		if (!mainImageContainer || !images) return

		titles.forEach((title) => {
			title.addEventListener('mouseenter', (e) => {
				const target = e.currentTarget as HTMLElement
				const titleName = target.dataset.title
				if (!titleName) return

				mainImageContainer.classList.add('gallery__active')

				images.forEach((img) => {
					img.classList.toggle(
						'img__visible',
						img.dataset.forTitle === titleName
					)
				})

				const onMouseMove = (e: MouseEvent) => {
					const parentRect:DOMRect = mainImageContainer.parentElement!.getBoundingClientRect();

					const rect:DOMRect = mainImageContainer.getBoundingClientRect();
					const halfWidth:number = rect.width / 2
					const halfHeight:number = rect.height + 45

					const x:number = e.clientX - parentRect.left - halfWidth
					const y:number = e.clientY - parentRect.top - halfHeight

					mainImageContainer.style.transform = `translate(${x}px, ${y}px)`
				}
				document.addEventListener('mousemove', onMouseMove)

				const onMouseLeave = () => {
					mainImageContainer.classList.remove('gallery__active')
					document.removeEventListener('mousemove', onMouseMove)
					target.removeEventListener('mouseleave', onMouseLeave)
				}

				target.addEventListener('mouseleave', onMouseLeave)
			})
		})
	})


}
